// Masraf ekle/duzenle dialog'u.

import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Receipt, X } from 'lucide-react';
import { Buton, Etiket, Girdi, Rozet, toast } from '@emanetx/ui';
import { MASRAF_KATEGORILERI, type MasrafKategori, type MasrafKaydi } from '@emanetx/sdk';
import { useMasrafGuncelle, useMasrafOlustur } from './api.js';

const KATEGORI_ETIKETI: Record<MasrafKategori, string> = {
  KIRA: 'Kira',
  ELEKTRIK: 'Elektrik',
  SU: 'Su',
  INTERNET: 'İnternet',
  BAKIM: 'Bakım/Onarım',
  TEMIZLIK: 'Temizlik',
  PERSONEL: 'Personel',
  VERGI: 'Vergi',
  SIGORTA: 'Sigorta',
  YAKIT: 'Yakıt',
  PAZARLAMA: 'Pazarlama',
  DIGER: 'Diğer',
};

function bugunIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function MasrafDialog({
  acik,
  onDegisim,
  noktaId,
  noktaKod,
  noktaAd,
  duzenlenen,
}: {
  acik: boolean;
  onDegisim: (v: boolean) => void;
  noktaId: string;
  noktaKod: string;
  noktaAd: string;
  duzenlenen?: MasrafKaydi | null;
}) {
  const [kategori, setKategori] = useState<MasrafKategori>('KIRA');
  const [tutar, setTutar] = useState(0);
  const [tarih, setTarih] = useState(bugunIso());
  const [aciklama, setAciklama] = useState('');
  const [fis, setFis] = useState('');

  useEffect(() => {
    if (duzenlenen) {
      setKategori(duzenlenen.kategori);
      setTutar(Number(duzenlenen.tutar));
      setTarih(duzenlenen.tarih);
      setAciklama(duzenlenen.aciklama ?? '');
      setFis(duzenlenen.fis ?? '');
    } else if (acik) {
      setKategori('KIRA');
      setTutar(0);
      setTarih(bugunIso());
      setAciklama('');
      setFis('');
    }
  }, [duzenlenen, acik]);

  const olustur = useMasrafOlustur();
  const guncelle = useMasrafGuncelle();

  const onayla = async () => {
    if (tutar <= 0) {
      toast.error("Tutar 0'dan büyük olmalı.");
      return;
    }
    try {
      if (duzenlenen) {
        await guncelle.mutateAsync({
          id: duzenlenen.id,
          giris: {
            kategori,
            tutar,
            tarih,
            aciklama: aciklama.trim() || null,
            fis: fis.trim() || null,
          },
        });
        toast.success('Masraf güncellendi');
      } else {
        await olustur.mutateAsync({
          noktaId,
          kategori,
          tutar,
          tarih,
          ...(aciklama.trim() && { aciklama: aciklama.trim() }),
          ...(fis.trim() && { fis: fis.trim() }),
        });
        toast.success('Masraf eklendi');
      }
      onDegisim(false);
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const bekliyor = olustur.isPending || guncelle.isPending;

  return (
    <Dialog.Root open={acik} onOpenChange={onDegisim}>
      <Dialog.Portal>
        <Dialog.Overlay className="bg-fume-900/50 fixed inset-0 z-40 backdrop-blur-sm" />
        <Dialog.Content className="dark:bg-fume-900 fixed left-1/2 top-1/2 z-50 w-[94vw] max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-6 shadow-2xl">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <Dialog.Title className="text-fume-900 dark:text-fume-50 flex items-center gap-2 text-base font-semibold">
                <Receipt size={16} className="text-marka-500" />
                {duzenlenen ? 'Masrafı Düzenle' : 'Yeni Masraf'}
              </Dialog.Title>
              <Dialog.Description asChild>
                <div className="text-fume-500 dark:text-fume-400 mt-1.5 flex flex-wrap items-center gap-2 text-sm">
                  <span className="truncate">{noktaAd}</span>
                  <Rozet ton="fume" className="font-mono">
                    {noktaKod}
                  </Rozet>
                </div>
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button className="text-fume-400 hover:text-fume-700" aria-label="Kapat">
                <X size={18} />
              </button>
            </Dialog.Close>
          </div>

          <div className="mt-5 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Etiket htmlFor="kategori">Kategori</Etiket>
                <select
                  id="kategori"
                  value={kategori}
                  onChange={(e) => setKategori(e.target.value as MasrafKategori)}
                  className="border-fume-200 focus:ring-marka-500 dark:border-fume-700 dark:bg-fume-900 dark:text-fume-50 h-11 w-full rounded-lg border bg-white px-3 text-sm focus:outline-none focus:ring-2"
                >
                  {MASRAF_KATEGORILERI.map((k) => (
                    <option key={k} value={k}>
                      {KATEGORI_ETIKETI[k]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Etiket htmlFor="tutar">Tutar (₺)</Etiket>
                <Girdi
                  id="tutar"
                  type="number"
                  step="0.01"
                  min={0}
                  value={tutar}
                  onChange={(e) => setTutar(Number(e.target.value) || 0)}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Etiket htmlFor="tarih">Tarih</Etiket>
              <Girdi
                id="tarih"
                type="date"
                value={tarih}
                onChange={(e) => setTarih(e.target.value || bugunIso())}
                max="2100-12-31"
                min="2020-01-01"
              />
              <p className="text-fume-500 text-xs">
                Geriye dönük tarih girilebilir. Karlılık raporu bu tarihin ayına göre gruplandırır.
              </p>
            </div>
            <div className="space-y-1.5">
              <Etiket htmlFor="aciklama">Açıklama (opsiyonel)</Etiket>
              <Girdi
                id="aciklama"
                placeholder="Örn: Mayıs ayı kira ödemesi"
                value={aciklama}
                onChange={(e) => setAciklama(e.target.value)}
                maxLength={500}
              />
            </div>
            <div className="space-y-1.5">
              <Etiket htmlFor="fis">Fiş/Fatura No (opsiyonel)</Etiket>
              <Girdi
                id="fis"
                placeholder="Örn: FAT-2026-0042"
                value={fis}
                onChange={(e) => setFis(e.target.value)}
                maxLength={120}
              />
            </div>
          </div>

          <div className="flex flex-col-reverse gap-2 pt-5 sm:flex-row sm:justify-end">
            <Dialog.Close asChild>
              <Buton stil="cizgili" disabled={bekliyor}>
                Vazgeç
              </Buton>
            </Dialog.Close>
            <Buton onClick={() => void onayla()} disabled={bekliyor || tutar <= 0}>
              {bekliyor ? 'Kaydediliyor…' : duzenlenen ? 'Güncelle' : 'Masraf Ekle'}
            </Buton>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export const MASRAF_KATEGORI_ETIKETI = KATEGORI_ETIKETI;
